import type { LandingContent } from "../content/types";
import { BrandMark } from "../components/BrandMark";
import { Container } from "../components/Container";
import { Icon } from "../components/Icon";
import { ComingSoonCTA } from "../components/ComingSoonCTA";

type HeroSectionProps = {
  content: LandingContent["hero"];
  secondaryHref: string;
  locale: LandingContent["locale"];
};

export function HeroSection({ content, secondaryHref, locale }: HeroSectionProps) {
  return (
    <section className="relative flex min-h-[calc(100vh-72px)] items-center overflow-hidden">
      <div className="luxoria-glow absolute right-0 top-[-160px] hidden h-[700px] w-[900px] lg:block" />
      <div className="luxoria-glow-cyan absolute left-[-200px] top-[320px] hidden h-[520px] w-[640px] lg:block" />
      <Container className="relative w-full py-16 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="mx-auto flex max-w-xl flex-col items-center gap-6 text-center lg:mx-0 lg:max-w-none lg:items-start lg:text-left">
            <div className="lg:hidden">
              <BrandMark size={96} />
            </div>
            <h1 className="font-display text-4xl font-bold leading-[1.08] text-text-primary sm:text-5xl lg:text-[56px]">
              {content.title}
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-text-secondary sm:text-[17px]">{content.subtitle}</p>
            <div className="mt-1 flex flex-col gap-3.5 sm:flex-row sm:items-center">
              <ComingSoonCTA label={content.primaryCta} locale={locale} page="landing" />
              <a
                href={secondaryHref}
                className="inline-flex items-center justify-center rounded-full border border-subtle px-6 py-3.5 text-[15px] font-medium text-text-primary transition-colors hover:bg-white/5"
              >
                {content.secondaryCta}
              </a>
            </div>
          </div>

          <HeroMock />
        </div>
      </Container>
    </section>
  );
}

function HeroMock() {
  return (
    <div className="relative mx-auto hidden h-[480px] w-full max-w-[420px] lg:block" aria-hidden="true">
      <div className="absolute left-1/2 top-10 w-[340px] -translate-x-1/2 rounded-[28px] border border-subtle bg-bg-card p-5 shadow-card">
        <div className="flex items-center justify-between">
          <span className="font-display text-base font-bold text-text-primary">Luxoria</span>
          <Icon name="settings" size={16} className="text-text-muted" />
        </div>
        <div className="mt-4 h-[150px] rounded-[18px] bg-gradient-to-br from-[#1E3A66] via-accent-blue to-accent-cyan" />
        <div className="mt-4 flex items-center gap-2.5 rounded-2xl bg-accent-blue px-4 py-3.5">
          <Icon name="images" size={18} className="text-on-accent" />
          <span className="text-sm font-semibold text-on-accent">Choose Images</span>
        </div>
        <div className="mt-3 flex items-center gap-2.5 rounded-2xl border border-subtle px-4 py-3.5">
          <Icon name="video" size={18} className="text-text-primary" />
          <span className="text-sm font-semibold text-text-primary">Choose Video</span>
        </div>
      </div>
      <div className="absolute left-0 top-0 w-[170px] rounded-[18px] border border-subtle bg-bg-card-2 p-3.5 shadow-float">
        <div className="h-[54px] rounded-xl bg-gradient-to-br from-[#1E3A66] to-accent-cyan" />
        <p className="mt-2 text-xs font-semibold text-text-primary">Photo HDR · HEIC</p>
      </div>
      <div className="absolute bottom-0 right-0 w-[210px] rounded-[18px] border border-subtle bg-bg-card-2 p-3.5 shadow-float">
        <div className="h-[54px] rounded-xl bg-gradient-to-br from-[#1E3A66] to-accent-cyan" />
        <p className="mt-2 text-xs font-semibold text-text-primary">Video HDR · HEVC Main10 HLG</p>
      </div>
    </div>
  );
}
